import React, { useMemo, useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion, Variants } from 'framer-motion';
import { Card, Button, Badge } from '../components/ui';
import { COURSES } from '../data/courses';
import { collection, query, where, onSnapshot, doc, orderBy, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase';
import { HomeBlock } from '../types';
import { ThermalHeatmap } from '../components/ThermalHeatmap';
import { SystemCheckFlow, SystemCheckinData } from '../components/SystemCheckFlow';
import { SystemDiagnostic } from '../components/SystemDiagnostic';
import { DiscoverHubMap } from '../components/DiscoverHubMap';
import { MONTHLY_TIPS } from '../data/daily_tips';

const Home = () => {
    const { userProfile, schoolProfile, refreshProfile } = useAuth();
    const navigate = useNavigate();

    const [activeBroadcast, setActiveBroadcast] = useState(false);
    const [scheduledBroadcast, setScheduledBroadcast] = useState<{name: string, time: string} | null>(null);
    const [homeBlocks, setHomeBlocks] = useState<HomeBlock[]>([]);
    const [unreadDMs, setUnreadDMs] = useState(0);

    // Tip of the Day calculation (changes at midnight based on local date string)
    const currentDayOfMonth = new Date().getDate();
    const tipIndex = (currentDayOfMonth - 1) % MONTHLY_TIPS.length;
    const dailyTip = MONTHLY_TIPS[tipIndex];

    // Referral Form State
    const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);
    const [referralForm, setReferralForm] = useState({ name: '', address: '', phone: '', email: '', description: '' });
    const [referralStatus, setReferralStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

    // Thermal Checkin State
    const [showThermometer, setShowThermometer] = useState(false);
    const [latestCheckinData, setLatestCheckinData] = useState<SystemCheckinData | null>(null);
    const [showIntervention, setShowIntervention] = useState(false);

    const [isHeatmapVisible, setIsHeatmapVisible] = useState(() => {
        const saved = localStorage.getItem('thermalHeatmapVisible');
        return saved !== null ? JSON.parse(saved) : false;
    });

    const toggleHeatmap = () => {
        const newVal = !isHeatmapVisible;
        setIsHeatmapVisible(newVal);
        localStorage.setItem('thermalHeatmapVisible', JSON.stringify(newVal));
    };

    const handleSystemCheckComplete = async (data: SystemCheckinData) => {
        if (!userProfile) return;

        try {
            const userRef = doc(db, 'users', userProfile.uid);
            await updateDoc(userRef, {
                thermalCheckins: arrayUnion({
                    id: Date.now().toString(),
                    timestamp: new Date().toISOString(),
                    ...data
                })
            });
            await refreshProfile();
            setLatestCheckinData(data);
            setShowThermometer(false);
            setShowIntervention(true); // Show Phase 3 & 4
        } catch (error) {
            console.error('Error saving system checkin:', error);
        }
    };

    const handleReferralSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        const subject = encodeURIComponent('New Client Referral Request');
        const body = encodeURIComponent(
            `Name: ${referralForm.name}\nEmail: ${referralForm.email}\nPhone: ${referralForm.phone}\nAddress: ${referralForm.address}\n\nDescription of Situation:\n${referralForm.description}`
        );
        
        window.location.href = `mailto:info@altalaw.co.uk?subject=${subject}&body=${body}`;
        
        setIsReferralModalOpen(false);
        setReferralForm({ name: '', address: '', phone: '', email: '', description: '' });
    };

    // Fetch broadcast status and pending connections
    useEffect(() => {
        const liveQuery = query(collection(db, 'broadcast_sessions'), where('status', '==', 'live'));
        const unsubLive = onSnapshot(liveQuery, (snap) => {
            setActiveBroadcast(!snap.empty);
        });

        const unsubSched = onSnapshot(doc(db, 'config', 'broadcast_schedule'), (snap) => {
            if (snap.exists()) {
                const data = snap.data();
                setScheduledBroadcast({
                    name: data.nextSpeaker || '',
                    time: data.scheduledAt || ''
                });
            }
        });

        const blockQuery = query(collection(db, 'home_blocks'), where('isActive', '==', true), orderBy('order', 'asc'));
        const unsubBlocks = onSnapshot(blockQuery, (snap) => {
            const blocks: HomeBlock[] = [];
            snap.forEach(d => blocks.push({ id: d.id, ...d.data() } as HomeBlock));
            setHomeBlocks(blocks);
        });

        let unsubPending = () => {};
        if (userProfile?.uid) {
            const qPending = query(
                collection(db, 'connections'),
                where('users', 'array-contains', userProfile.uid),
                where('status', '==', 'pending'),
                where('receiverUid', '==', userProfile.uid)
            );
            unsubPending = onSnapshot(qPending, (snap) => {
                setUnreadDMs(snap.size); 
            });
        }

        return () => {
            unsubLive();
            unsubSched();
            unsubBlocks();
            unsubPending();
        };
    }, [userProfile?.uid]);

    // Determine the user's next step in the roadmap
    const nextStepInfo = useMemo(() => {
        if (!userProfile) return null;
        
        let inProgressCourse = null;
        let nextIncompleteCourse = null;

        for (const course of COURSES) {
            const progress = userProfile.progress?.[course.id];
            
            if (progress && !progress.quizPassed && progress.completedModules?.length > 0) {
                inProgressCourse = { ...course, progressInfo: progress };
                break; // Found the one currently being worked on
            }
            if (!progress || (!progress.quizPassed && (!progress.completedModules || progress.completedModules.length === 0))) {
                if (!nextIncompleteCourse) nextIncompleteCourse = course; // Found the first unstarted one
            }
        }

        const targetCourse = inProgressCourse || nextIncompleteCourse || COURSES[0];
        const targetCourseProgress = (targetCourse as any).progressInfo;
        
        let displayPercent = 0;
        if (targetCourseProgress) {
            displayPercent = Math.round(((targetCourseProgress.completedModules?.length || 0) / targetCourse.totalModules) * 100);
        }

        return {
            course: targetCourse,
            percent: displayPercent,
            isInProgress: !!inProgressCourse
        };
    }, [userProfile]);

    const isPaid = userProfile?.isPaid === true || userProfile?.subscriptionStatus === 'active' || schoolProfile?.isPaid === true || userProfile?.role === 'admin' || userProfile?.schoolId === 'dadsmatter';
    const isProfessional = userProfile?.role === 'admin' || userProfile?.role === 'manager' || userProfile?.role === 'staff' || userProfile?.schoolId === 'dadsmatter';

    const hasDramaticFluctuation = useMemo(() => {
        if (!userProfile?.thermalCheckins || userProfile.thermalCheckins.length < 2) return false;
        
        const sortedCheckins = [...userProfile.thermalCheckins].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        
        const recentCheckins = sortedCheckins.filter(c => new Date(c.timestamp) >= sevenDaysAgo);
        if (recentCheckins.length > 1) {
            let minMood = recentCheckins[0].points?.[0]?.x ?? recentCheckins[0].value;
            let maxMood = minMood;

            recentCheckins.forEach(c => {
                const moodVal = c.points?.[0]?.x ?? c.value;
                if (moodVal < minMood) minMood = moodVal;
                if (moodVal > maxMood) maxMood = moodVal;
            });
            
            const gap = Math.abs(maxMood - minMood);
            return gap >= 6;
        }
        return false;
    }, [userProfile?.thermalCheckins]);

    const visibleBlocks = useMemo(() => {
        return homeBlocks.filter(block => {
            if (block.visibility === 'free_only' && (isPaid || isProfessional)) return false;
            if (block.visibility === 'paid_only' && (!isPaid && !isProfessional)) return false;
            return true;
        });
    }, [homeBlocks, isPaid, isProfessional]);

    const getBlockStyle = (style: HomeBlock['colorStyle']) => {
        switch (style) {
            case 'indigo': return 'bg-indigo-50 border-indigo-100 border text-slate-900';
            case 'emerald': return 'bg-emerald-50 border-emerald-100 border text-emerald-900';
            case 'amber': return 'bg-amber-50 border-amber-100 border text-amber-900';
            case 'rose': return 'bg-rose-50 border-rose-100 border text-rose-900';
            case 'dark': return 'bg-slate-900 text-white border-0';
            case 'gradient_teal': return 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-0';
            default: return 'bg-white border-slate-200 border text-slate-900';
        }
    };

    const containerVariants: Variants = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: { staggerChildren: 0.1 }
        }
    };

    const itemVariants: Variants = {
        hidden: { opacity: 0, y: 20 },
        show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } }
    };

    return (
        <Layout>
            
            <div className="max-w-5xl mx-auto w-full pt-4 pb-12">
                <motion.div 
                    initial="hidden"
                    animate="show"
                    variants={containerVariants}
                    className="space-y-6"
                >
                    {/* Welcome Banner: M3 Hero Card */}
                    <motion.div variants={itemVariants}>
                        <div className="bg-gradient-to-br from-indigo-900 via-slate-900 to-indigo-950 rounded-[32px] p-8 md:p-12 text-white relative overflow-hidden shadow-xl border border-white/10">
                            
                            {/* Decorative background elements */}
                            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/20 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/3"></div>
                            <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-[60px] translate-y-1/2 -translate-x-1/2"></div>
                            
                            <div className="relative z-10 flex flex-col md:flex-row gap-8 justify-between items-start md:items-center">
                                
                                {/* Left side: Text & Badges */}
                                <div className="space-y-6 flex-1 max-w-2xl">
                                    {/* Logo & Badges Row */}
                                        <div className="flex flex-wrap items-center gap-3">
                                        
                                        {isPaid || isProfessional ? (
                                            <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 text-xs font-bold tracking-widest uppercase shadow-sm">
                                                PRO
                                            </span>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <span className="px-3 py-1 rounded-full bg-slate-800 text-slate-300 border border-white/10 text-xs font-bold tracking-widest uppercase">
                                                    Free Tier
                                                </span>
                                                <button 
                                                    onClick={() => navigate('/subscription')}
                                                    className="bg-indigo-500 hover:bg-indigo-400 text-white px-3 py-1 rounded-full text-xs font-bold transition-all flex items-center gap-1 shadow-md hover:shadow-lg active:scale-95"
                                                >
                                                    Upgrade <span className="text-amber-300 ml-1">⭐</span>
                                                </button>
                                            </div>
                                        )}

                                        {unreadDMs > 0 && (
                                            <button 
                                                onClick={() => navigate('/messages')} 
                                                className="bg-rose-500 hover:bg-rose-400 text-white px-3 py-1 rounded-full text-xs font-bold tracking-widest uppercase hover:shadow-md transition-all flex items-center gap-2 animate-pulse"
                                            >
                                                <span className="w-1.5 h-1.5 rounded-full bg-white"></span>
                                                {unreadDMs} New Connection{unreadDMs > 1 ? 's' : ''}
                                            </button>
                                        )}
                                    </div>

                                    {/* Title & Description */}
                                    <div>
                                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-black tracking-tight mb-4 text-white">
                                            Welcome back, {userProfile?.displayName?.split(' ')[0] || 'Dad'}.
                                        </h1>
                                        <p className="text-indigo-100/80 text-lg md:text-xl font-medium leading-relaxed max-w-xl">
                                            A community of fathers mastering the strategy, law, and mindset required to protect their children.
                                        </p>
                                    </div>
                                    
                                    {/* Action text link */}
                                    <button 
                                        onClick={async () => {
                                            const shareData = {
                                                title: 'Dads Matter Hub',
                                                text: 'Join me on Dads Matter Hub - the ultimate community for fathers mastering Family Law strategy and mindset.',
                                                url: 'https://training.dadsmattercreatives.org.uk/'
                                            };
                                            if (navigator.share) {
                                                try {
                                                    await navigator.share(shareData);
                                                } catch (err) {
                                                    console.error('Error sharing', err);
                                                }
                                            } else {
                                                navigator.clipboard.writeText(shareData.url);
                                                alert('Link copied to clipboard!');
                                            }
                                        }}
                                        className="text-sm font-bold text-indigo-300 hover:text-white transition-colors flex items-center gap-2 group w-fit"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>
                                        Refer a Friend
                                    </button>
                                </div>

                                {/* Right side: Quick Actions Grid */}
                                <div className="flex flex-col sm:flex-row md:flex-col gap-3 w-full md:w-auto shrink-0 z-10">
                                    <button 
                                        onClick={() => setShowThermometer(true)} 
                                        className="group relative bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/10 text-white px-6 py-5 rounded-2xl transition-all shadow-lg active:scale-95 flex items-center gap-4 w-full md:w-64"
                                    >
                                        <div className="w-12 h-12 rounded-full bg-indigo-500/20 flex items-center justify-center text-2xl shrink-0 border border-indigo-500/30">
                                            🌡️
                                        </div>
                                        <div className="text-left">
                                            <div className="font-bold text-sm tracking-wide text-white">System Check</div>
                                            {userProfile?.thermalCheckins && userProfile.thermalCheckins.length > 0 ? (
                                                <div className="text-[10px] text-amber-300 font-bold tracking-widest uppercase mt-0.5">
                                                    🔥 {Math.min(3, userProfile.thermalCheckins.length)}-Day Streak
                                                </div>
                                            ) : (
                                                <div className="text-[10px] text-indigo-200 font-medium tracking-widest uppercase mt-0.5">Takes 60 Seconds</div>
                                            )}
                                        </div>
                                    </button>

                                    <button 
                                        onClick={() => navigate('/buddy-matcher')} 
                                        className="group relative bg-white/5 hover:bg-white/10 backdrop-blur-md border border-white/5 text-white px-6 py-5 rounded-2xl transition-all shadow-lg active:scale-95 flex items-center gap-4 w-full md:w-64"
                                    >
                                        <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center text-2xl shrink-0 border border-cyan-500/30">
                                            🤝
                                        </div>
                                        <div className="text-left">
                                            <div className="font-bold text-sm tracking-wide text-white">Request Support</div>
                                            <div className="text-[10px] text-indigo-200 font-medium tracking-widest uppercase mt-0.5">Direct Peer Help</div>
                                        </div>
                                    </button>
                                </div>
                                
                            </div>
                        </div>
                    </motion.div>

                    {/* Dramatic Fluctuation Warning (M3 Variant) */}
                    {hasDramaticFluctuation && (
                        <motion.div variants={itemVariants}>
                            <Card className="bg-md-error-container text-md-on-error-container border-0 shadow-sm">
                                <div className="p-5 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-4">
                                    <div className="w-12 h-12 rounded-full bg-white/50 flex items-center justify-center text-2xl shrink-0">
                                        ⚠️
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-bold text-lg mb-1">We've noticed significant shifts in your temperature</h3>
                                        <p className="opacity-90 text-sm">Your recent thermal check-ins show wide fluctuations. This often indicates high stress or emotional overwhelm. You don't have to navigate this alone.</p>
                                    </div>
                                    <Button 
                                        variant="danger"
                                        onClick={() => navigate('/buddy-matcher')} 
                                        className="shadow-sm whitespace-nowrap"
                                    >
                                        Request Support
                                    </Button>
                                </div>
                            </Card>
                        </motion.div>
                    )}

                    <motion.div variants={itemVariants}>
                        {isHeatmapVisible ? (
                            <div className="relative group/heatmap">
                                <button 
                                    onClick={toggleHeatmap}
                                    className="absolute top-6 right-6 z-20 text-slate-400 hover:text-rose-500 transition-colors bg-white/50 backdrop-blur-sm rounded-full p-2 border border-slate-200 shadow-sm opacity-100 md:opacity-0 md:group-hover/heatmap:opacity-100"
                                    title="Hide Thermal Rhythm"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                                </button>
                                <ThermalHeatmap checkins={userProfile?.thermalCheckins || []} />
                            </div>
                        ) : (
                            <Card className="p-4 flex items-center justify-between bg-white border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors shadow-sm" onClick={toggleHeatmap}>
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center">🌡️</div>
                                    <h3 className="text-sm font-bold text-slate-700 font-heading">Thermal Rhythm is hidden</h3>
                                </div>
                                <div className="flex items-center gap-2 text-xs font-bold text-indigo-600 uppercase tracking-wider">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                    Show
                                </div>
                            </Card>
                        )}
                    </motion.div>
                    
                    <motion.div variants={itemVariants}>
                        <DiscoverHubMap />
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-6">
                        
                        {/* Left Column: Core Focus & Roadmap */}
                        <div className="md:col-span-8 space-y-6">
                            
                            {/* Next Step Card */}
                            <motion.div variants={itemVariants}>
                                <div className="flex items-center gap-3 mb-4 mt-2">
                                    <h2 className="text-xl font-bold text-slate-900 font-heading">Your Roadmap</h2>
                                    <div className="h-px bg-slate-200 flex-1"></div>
                                </div>
                                <Card hover onClick={() => navigate('/dashboard')} className="p-0 overflow-hidden cursor-pointer border-0 shadow-sm">
                                    <div className="bg-md-surface-container-low p-6 transition-colors hover:bg-md-surface-variant">
                                        <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
                                            <div className="w-16 h-16 rounded-2xl bg-md-primary text-md-on-primary flex items-center justify-center text-3xl shadow-sm shrink-0">
                                                {nextStepInfo?.course?.icon || '🎯'}
                                            </div>
                                            <div className="flex-1 text-center md:text-left">
                                                <Badge color={nextStepInfo?.isInProgress ? 'yellow' : 'blue'}>
                                                    {nextStepInfo?.isInProgress ? 'In Progress' : 'Up Next'}
                                                </Badge>
                                                <h3 className="text-xl font-bold text-md-on-surface mt-2 mb-1 tracking-tight">{nextStepInfo?.course?.title}</h3>
                                                <p className="text-sm text-md-on-surface-variant line-clamp-2 leading-relaxed mb-5 font-medium">{nextStepInfo?.course?.description}</p>
                                                
                                                <div className="flex items-center gap-4">
                                                    <div className="flex-1 max-w-[200px] mx-auto md:mx-0">
                                                        <div className="flex justify-between text-[10px] font-bold text-md-on-surface-variant mb-1.5 uppercase tracking-widest">
                                                            <span>Progress</span>
                                                            <span>{nextStepInfo?.percent}%</span>
                                                        </div>
                                                        <div className="w-full bg-md-surface-container-highest rounded-full h-1.5 overflow-hidden">
                                                            <div className="bg-md-primary h-1.5 rounded-full" style={{ width: `${nextStepInfo?.percent}%` }}></div>
                                                        </div>
                                                    </div>
                                                    <Button onClick={(e) => { e.stopPropagation(); navigate(`/course/${nextStepInfo?.course?.id}`); }} className="shrink-0">
                                                        {nextStepInfo?.isInProgress ? 'Continue' : 'Start Topic'}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            </motion.div>

                            {/* Promotional / Services Area */}
                            <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                {visibleBlocks.map(block => {
                                    const isDark = block.colorStyle === 'dark' || block.colorStyle === 'gradient_teal' || !!block.backgroundImageUrl;
                                    
                                    const blockStyleObj: React.CSSProperties = block.backgroundImageUrl ? {
                                        backgroundImage: `linear-gradient(to bottom, rgba(15, 23, 42, 0.4), rgba(15, 23, 42, 0.9)), url(${block.backgroundImageUrl})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        backgroundRepeat: 'no-repeat'
                                    } : {};

                                    const handleBlockClick = () => {
                                        if (block.title.toLowerCase().includes('alta') || block.actionUrl.toLowerCase().includes('alta')) {
                                            setIsReferralModalOpen(true);
                                        } else if (block.actionUrl.startsWith('http')) {
                                            window.open(block.actionUrl, '_blank', 'noopener,noreferrer');
                                        } else {
                                            navigate(block.actionUrl);
                                        }
                                    };

                                    return (
                                        <Card 
                                            key={block.id} 
                                            hover 
                                            onClick={handleBlockClick}
                                            className={`p-6 flex flex-col relative overflow-hidden group cursor-pointer ${block.backgroundImageUrl ? 'text-white border-0 shadow-lg' : getBlockStyle(block.colorStyle)}`}
                                            style={blockStyleObj}
                                        >
                                            {/* Decorative Background Elements */}
                                            {/* Decorative Background Elements (Only if no custom image) */}
                                            {!block.backgroundImageUrl && block.colorStyle === 'dark' && (
                                                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/20 rounded-full blur-[40px] -translate-y-1/2 translate-x-1/2"></div>
                                            )}
                                            {!block.backgroundImageUrl && block.colorStyle === 'gradient_teal' && (
                                                <div className="absolute bottom-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-[30px] translate-y-1/2 translate-x-1/2"></div>
                                            )}
                                            {['indigo', 'emerald', 'amber', 'rose'].includes(block.colorStyle) && !block.backgroundImageUrl && (
                                                <div className="absolute inset-0 bg-white/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                            )}

                                            <div className="relative z-10 flex flex-col h-full items-start">
                                                <div className="flex justify-end w-full items-start mb-2">
                                                    {block.badgeText && (
                                                        <Badge color={(block.badgeColor as any) || 'blue'}>{block.badgeText}</Badge>
                                                    )}
                                                </div>
                                                
                                                <h3 className="text-xl font-bold mb-2 font-heading">{block.title}</h3>
                                                <p className={`text-sm mb-4 flex-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                                                    {block.description}
                                                </p>
                                                
                                                <div className={`mt-auto pt-4 w-full flex items-center justify-between border-t ${isDark ? 'border-white/10' : 'border-black/5'}`}>
                                                    <span className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-white' : 'text-slate-900 group-hover:text-indigo-600'} transition-colors`}>
                                                        {block.actionText} →
                                                    </span>
                                                </div>
                                            </div>
                                        </Card>
                                    );
                                })}

                                {/* Static Chat Card (Always visible) */}
                                <Card hover className="p-6 flex flex-col items-center text-center justify-center bg-md-secondary-container text-md-on-secondary-container border-0 relative overflow-hidden group cursor-pointer" onClick={() => navigate('/hub')}>
                                    <div className="w-14 h-14 bg-md-surface rounded-full flex items-center justify-center text-2xl shadow-sm mb-4 relative z-10 transition-transform group-hover:scale-110 duration-300">💬</div>
                                    <h3 className="text-xl font-bold mb-2 relative z-10 font-heading">Dads Hub Chat</h3>
                                    <p className="opacity-80 text-sm relative z-10 font-medium">Connect, vent, and strategize anonymously in real-time with peers.</p>
                                </Card>
                            </motion.div>
                        </div>

                        {/* Right Column: Timely Info & Events */}
                        <div className="md:col-span-4 space-y-6">
                            
                            {/* Live Event Widget */}
                            <motion.div variants={itemVariants}>
                                <div className="flex items-center gap-3 mb-4 mt-2">
                                    <h2 className="text-xl font-bold text-slate-900 font-heading">Live Seminars</h2>
                                </div>
                                <Card className={`overflow-hidden border ${activeBroadcast ? 'border-md-error ring-2 ring-md-error-container shadow-md' : 'border-md-outline-variant shadow-sm'}`}>
                                    {activeBroadcast ? (
                                        <div className="p-6 bg-md-error-container text-md-on-error-container relative overflow-hidden">
                                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-md-error text-md-on-error text-[10px] font-bold tracking-widest uppercase mb-4">
                                                <span className="w-2 h-2 rounded-full bg-md-on-error animate-pulse"></span>
                                                Live Now
                                            </div>
                                            <h3 className="font-bold text-lg mb-2">Seminar in Progress</h3>
                                            <p className="text-sm opacity-90 mb-6 font-medium line-clamp-2">Join the live room to ask your questions direct to our speakers.</p>
                                            <Button variant="danger" className="w-full" onClick={() => navigate('/broadcast')}>Enter Live Room</Button>
                                        </div>
                                    ) : (
                                        <div className="p-6 bg-md-surface">
                                            <div className="w-12 h-12 rounded-full bg-md-surface-variant flex items-center justify-center text-xl mb-4">🎙️</div>
                                            <h3 className="font-bold text-md-on-surface mb-2 tracking-tight">Upcoming Event</h3>
                                            {scheduledBroadcast?.name ? (
                                                <div className="bg-md-surface-container-low rounded-2xl p-4 mt-3">
                                                    <p className="text-[10px] font-bold text-md-primary uppercase tracking-widest mb-1.5">Speaker Topic</p>
                                                    <p className="text-sm font-bold text-md-on-surface line-clamp-1">{scheduledBroadcast.name}</p>
                                                    <p className="text-xs text-md-on-surface-variant mt-2 flex items-center gap-1.5 font-medium">⏱️ {scheduledBroadcast.time}</p>
                                                </div>
                                            ) : (
                                                <p className="text-sm text-md-on-surface-variant font-medium mt-1">No events currently scheduled. Check back later.</p>
                                            )}
                                        </div>
                                    )}
                                </Card>
                            </motion.div>

                            {/* Advice / Tip of the Day */}
                            <motion.div variants={itemVariants}>
                                <div className="flex items-center gap-3 mb-4 mt-2">
                                    <h2 className="text-xl font-bold text-slate-900 font-heading">Sound Advice</h2>
                                </div>
                                <Card className="bg-gradient-to-b from-slate-800 to-slate-900 text-white border-slate-800 relative overflow-hidden">
                                    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiLz48L3N2Zz4=')] opacity-50"></div>
                                    <div className="p-6 relative z-10">
                                        <div className="flex justify-between items-start mb-4">
                                            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center text-xl backdrop-blur-sm border border-white/5 shadow-inner">⚖️</div>
                                            <span className="text-[10px] uppercase font-black tracking-widest text-indigo-300 bg-indigo-500/20 px-2 py-1 rounded">Tip of the day</span>
                                        </div>
                                        <h3 className="font-bold text-lg mb-2 font-heading">{dailyTip.title}</h3>
                                        <p className="text-slate-300 text-sm italic leading-relaxed font-medium">
                                            {dailyTip.body}
                                        </p>
                                        <div className="mt-4 flex flex-wrap gap-2">
                                            {dailyTip.tags.map((tag, i) => (
                                                <span key={i} className="text-[9px] font-bold uppercase tracking-widest text-slate-500 bg-white/5 px-2 py-0.5 rounded">{tag}</span>
                                            ))}
                                        </div>
                                    </div>
                                </Card>
                            </motion.div>

                        </div>
                    </div>
                </motion.div>
            </div>

            {/* System Check Flow Modal */}
            {showThermometer && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-md-on-surface/40 backdrop-blur-sm">
                     <SystemCheckFlow 
                         onComplete={handleSystemCheckComplete}
                         onCancel={() => setShowThermometer(false)}
                     />
                </div>
            )}

            {/* Diagnostic Intervention Modal */}
            {showIntervention && latestCheckinData && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-md-on-surface/40 backdrop-blur-sm">
                    <SystemDiagnostic 
                        data={latestCheckinData!} 
                        onClose={() => setShowIntervention(false)} 
                    />
                </div>
            )}

            {/* Referral Modal */}
            {isReferralModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-fade-in">
                    <Card className="max-w-md w-full p-6 shadow-2xl relative">
                        <button 
                            onClick={() => setIsReferralModalOpen(false)}
                            className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
                        >
                            ✕
                        </button>
                        
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center text-xl">⚖️</div>
                            <div>
                                <h2 className="text-xl font-bold text-slate-900 font-heading">Alta Legal Referral</h2>
                                <p className="text-xs text-slate-500">Submit your details for expert legal support.</p>
                            </div>
                        </div>

                        {referralStatus === 'success' ? (
                            <div className="bg-emerald-50 text-emerald-700 p-4 rounded-xl text-center font-bold animate-slide-up">
                                Preparing your email... Please confirm and send from your mail client.
                            </div>
                        ) : (
                            <form onSubmit={handleReferralSubmit} className="space-y-4">
                                {referralStatus === 'error' && (
                                    <div className="bg-rose-50 text-rose-700 p-3 rounded-lg text-sm">
                                        There was a problem submitting your referral. Please try again.
                                    </div>
                                )}
                                
                                <div className="space-y-3">
                                    <input 
                                        type="text" required placeholder="Full Name" 
                                        value={referralForm.name} onChange={e => setReferralForm({...referralForm, name: e.target.value})}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all"
                                    />
                                    <input 
                                        type="email" required placeholder="Email Address" 
                                        value={referralForm.email} onChange={e => setReferralForm({...referralForm, email: e.target.value})}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all"
                                    />
                                    <input 
                                        type="tel" required placeholder="Phone Number" 
                                        value={referralForm.phone} onChange={e => setReferralForm({...referralForm, phone: e.target.value})}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all"
                                    />
                                    <input 
                                        type="text" required placeholder="Your Address" 
                                        value={referralForm.address} onChange={e => setReferralForm({...referralForm, address: e.target.value})}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all"
                                    />
                                    <textarea 
                                        required placeholder="Brief description of your situation..." 
                                        value={referralForm.description} onChange={e => setReferralForm({...referralForm, description: e.target.value})}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all min-h-[100px] resize-none"
                                    ></textarea>
                                </div>
                                
                                <Button 
                                    type="submit" 
                                    disabled={referralStatus === 'submitting'}
                                    className="w-full flex items-center justify-center gap-2 mt-4"
                                >
                                    {referralStatus === 'submitting' ? 'Submitting...' : 'Submit Referral'}
                                </Button>
                            </form>
                        )}
                    </Card>
                </div>
            )}
        </Layout>
    );
};

export default Home;
